import {ActorListEditor} from '@github-ui/actor-list-editor'
import type {ActorListEditorProps} from '@github-ui/actor-list-editor/types'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import {useCallback, useMemo, useState} from 'react'

import type {AllowedActor, RegisteredRuleSchemaComponent, RulesetRoutePayload} from '../../types/rules-types'

type ActorMetadata = {
  actors: Record<string, AllowedActor>
}

export function ActorSelector({
  field,
  value,
  onValueChange,
  readOnly,
  metadata,
  queryMetadata,
}: RegisteredRuleSchemaComponent) {
  if (!Array.isArray(value)) {
    throw new Error('Value must be an array')
  }

  const {baseAvatarUrl} = useRoutePayload<RulesetRoutePayload>()

  const [actorMetadata, setActorMetadata] = useState<Record<string, AllowedActor>>(
    (metadata as ActorMetadata)?.actors || {},
  )

  // Hydrate `value: string[]` (relay IDs) into `Actor[]` for the editor.
  const actors = useMemo(
    () => value.map(id => actorMetadata[id]).filter((a): a is AllowedActor => a !== undefined),
    [value, actorMetadata],
  )

  const fetchSuggestions = useCallback(
    async (query: string): Promise<AllowedActor[]> => {
      const result = await queryMetadata?.(`actor_suggestions:${query}`)
      const suggestions = (result as {actors: AllowedActor[]})?.actors || []
      // Hydrate the local cache so later renders can resolve relay IDs to actors.
      const byId = suggestions.reduce<Record<string, AllowedActor>>((acc, a) => {
        acc[a.globalRelayId] = a
        return acc
      }, {})
      setActorMetadata(existing => ({...existing, ...byId}))
      return suggestions
    },
    [queryMetadata],
  )

  // Pre-fetch the canonical category list. Returns a stable reference per
  // queryMetadata so the editor's effect only runs once.
  const fetchCategories = useCallback(async (): Promise<string[]> => {
    const result = await queryMetadata?.('actor_categories')
    const categories = (result as {categories?: string[]})?.categories
    if (!Array.isArray(categories)) {
      // Force the editor into "categories unavailable" mode.
      throw new Error('actor_categories returned no list')
    }
    return categories
  }, [queryMetadata])

  const onAdd = useCallback<NonNullable<ActorListEditorProps<AllowedActor>['onAdd']>>(
    actor => {
      // Server payloads should always include a global ID, but they are cast rather than validated,
      // so guard against a missing global ID to avoid pushing `undefined` into `value`.
      if (!actor.globalRelayId) return
      onValueChange?.([...value, actor.globalRelayId])
    },
    [onValueChange, value],
  )

  const onRemove = useCallback<NonNullable<ActorListEditorProps<AllowedActor>['onRemove']>>(
    actor => {
      if (!actor.globalRelayId) return
      onValueChange?.(value.filter(id => id !== actor.globalRelayId))
    },
    [onValueChange, value],
  )

  return (
    <ActorListEditor<AllowedActor>
      title={field.display_name}
      readOnly={readOnly}
      actors={actors}
      baseAvatarUrl={baseAvatarUrl}
      selector={{fetchSuggestions, panelPlaceholder: 'Select actors'}}
      categories={{fetchCategories}}
      onAdd={onAdd}
      onRemove={onRemove}
    />
  )
}
